import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Layout from "@/components/layout/Layout";
import AuthGuard from "@/components/auth/AuthGuard";
import AdminGuard from "@/components/auth/AdminGuard";
import ControllerGuard from "@/components/auth/ControllerGuard";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Exams from "@/pages/Exams";
import ExamDetail from "@/pages/ExamDetail";
import TakeExam from "@/pages/TakeExam";
import ExamResultsPage from "@/pages/ExamResults";
import ExamReviewPage from "@/pages/ExamReview";
import History from "@/pages/History";
import Categories from "@/pages/Categories";
import CategoryDetail from "@/pages/CategoryDetail";
import Profile from "@/pages/Profile";
import AdminLayout from "@/pages/Admin/AdminLayout";
import AdminDashboard from "@/pages/Admin/AdminDashboard";
import AdminUsers from "@/pages/Admin/AdminUsers";
import AdminQuestions from "@/pages/Admin/AdminQuestions";
import AdminExams from "@/pages/Admin/AdminExams";
import AdminCategories from "@/pages/Admin/AdminCategories";
import ControllerLayout from "@/pages/Controller/ControllerLayout";
import ControllerExams from "@/pages/Controller/ControllerExams";
import ControllerExamDetail from "@/pages/Controller/ControllerExamDetail";
import TrafficSignsQuiz from "@/pages/TrafficSignsQuiz";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import FreeTrialStart from "@/pages/FreeTrialStart";
import Subscription from "@/pages/Subscription";
import AdminFreeTrial from "@/pages/Admin/AdminFreeTrial";
import NotFound from "@/pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/exams" element={<Exams />} />
            <Route path="/exams/:id" element={<ExamDetail />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/traffic-signs-quiz" element={<TrafficSignsQuiz />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route
              path="/free-trial"
              element={
                <AuthGuard>
                  <FreeTrialStart />
                </AuthGuard>
              }
            />
            <Route
              path="/categories/:slug"
              element={
                <AuthGuard>
                  <CategoryDetail />
                </AuthGuard>
              }
            />

            {/* Protected routes */}
            <Route
              path="/exams/:id/take"
              element={
                <AuthGuard>
                  <TakeExam />
                </AuthGuard>
              }
            />
            <Route
              path="/attempts/:attemptId/results"
              element={
                <AuthGuard>
                  <ExamResultsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/attempts/:attemptId/review"
              element={
                <AuthGuard>
                  <ExamReviewPage />
                </AuthGuard>
              }
            />
            <Route
              path="/history"
              element={
                <AuthGuard>
                  <History />
                </AuthGuard>
              }
            />
            <Route
              path="/profile"
              element={
                <AuthGuard>
                  <Profile />
                </AuthGuard>
              }
            />
            <Route
              path="/subscription"
              element={
                <AuthGuard>
                  <Subscription />
                </AuthGuard>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <AdminGuard>
                  <AdminLayout />
                </AdminGuard>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="questions" element={<AdminQuestions />} />
              <Route path="exams" element={<AdminExams />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="free-trial" element={<AdminFreeTrial />} />
            </Route>

            {/* Controller routes */}
            <Route
              path="/controller"
              element={
                <ControllerGuard>
                  <ControllerLayout />
                </ControllerGuard>
              }
            >
              <Route index element={<ControllerExams />} />
              <Route path="exams/:examId" element={<ControllerExamDetail />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
